import CONFIG from '../config/config';
import { Customer } from './CustomersAPI';
import { PaymentStatus } from '../types/payment-statuses';
import { instanceWithV2 as instance } from './SimpleAPIClient';

const serializeParamsForSpringBoot = (params: Record<string, unknown>): string => {
  const parts: string[] = [];
  for (const key in params) {
    if (Object.prototype.hasOwnProperty.call(params, key)) {
      const value = params[key];
      if (Array.isArray(value)) {
        value.forEach((item) => {
          parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(item))}`);
        });
      } else if (value !== null && value !== undefined) {
        parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`);
      }
    }
  }
  return parts.join('&');
};

export type OrderDress = {
  dressPlanId: string,
  name: string,
  type?: 'CATALOG_ITEM' | 'CATALOG_ITEM_VARIANT' | 'PRODUCTION_ITEM',
  collectionName?: string
}

type BaseOrder = {
  clientName: string,
  creationTime?: string,
  id?: number,
  attachmentUrls?: string[],
  lastUpdateTime?: string,
  notes?: string,
  files?: File[];
  weddingDate?: string,
  invoiceNumber?: string,
}

export type Order = InternalOrder & {
  dress?: OrderDress,
  sentToShop?: boolean
}

export type InternalOrder = BaseOrder & {
  disable: boolean,
  shopName?: string,
  linkedShopId?: number,
  dressDescription?: string,
  deliveryDate: string,
  visible: boolean,
  prioritized: boolean,
  pendingMeasures: boolean,
  paymentStatus?: PaymentStatus,
  shop?: string,
  shopIdV2?: string,
  status?: string,
  type?: string,
  customer?: Customer;
}

export type ExternalOrder = BaseOrder & {
  b2bType: string;
  itemId?: string;
  itemName: string;
  collectionName: string;
  receiverShopName: string;
  requesterShopName: string;
  inStoreDate: string;
  to: string;
  status: string;
  paymentStatus?: PaymentStatus;
  deletableFrom?: number;
  creationTime?: string;
  toOrderId?: number;
}

export function isInternalOrder(order: any): order is InternalOrder {
  return (order as InternalOrder).disable !== undefined;
}

export function isExternalOrder(order: any): order is ExternalOrder {
  return (order as ExternalOrder).to !== undefined;
}

export type FileUploadResponse = {
  url: string;
};

export type BatchOrderResponse = {
  message: string;
  data: Order[];
};

export type FileUploadRequest = {
  filename: string;
  file: FormData;
};

export type SendOrderEmail = {
  providersWithoutEmail: string[];
}

type BodyApiResponse = Order[];

const OrdersAPI = {
  get: (date?: string, shopId?: string, hidden?: boolean, status?: string, paymentStatus?: string) => {
    const queryParams = new URLSearchParams();
    if (date) queryParams.append("date", date);
    if (shopId) queryParams.append("shopId", shopId);
    if (hidden) queryParams.append("completed", "true");
    if (status) queryParams.append("status", status);
    if (paymentStatus) queryParams.append("paymentStatus", paymentStatus);
    return instance(CONFIG.API_ENDPOINT).get<BodyApiResponse>(`v1/shops/orders${!!queryParams.toString() ? `?${queryParams.toString()}` : ''}`)
  },
  getExternalOrders: (date?: string, shopId?: string, shopIds?: string[], unresolved?: boolean, statuses?: string[], paymentStatuses?: string[], paymentStatus?: string, page?: number, size?: number) => {
    const params: Record<string, unknown> = {};
    if (date) params.date = date;
    if (shopIds && shopIds.length > 0) params.shopIds = shopIds;
    else if (shopId) params.shopId = shopId;
    if (unresolved) params.unresolved = true;
    if (statuses && statuses.length > 0) params.statuses = statuses;
    if (paymentStatuses && paymentStatuses.length > 0) params.paymentStatuses = paymentStatuses;
    else if (paymentStatus) params.paymentStatus = paymentStatus;
    if (page !== undefined && size !== undefined) {
      params.page = page;
      params.size = size;
    }
    const query = serializeParamsForSpringBoot(params);
    return instance(CONFIG.API_ENDPOINT).get<any>(`v1/shops/orders/in-out${query ? `?${query}` : ''}`)
  },
  search: (value: string) => instance(CONFIG.API_ENDPOINT).get<BodyApiResponse>(`v1/shops/orders?search=${value}`),
  getInternalOrderById: (orderId: string) =>
    instance(CONFIG.API_ENDPOINT).get<Order>(`v1/shops/orders/${orderId}`),
  getExternalOrderById: (orderId: string) =>
    instance(CONFIG.API_ENDPOINT).get<ExternalOrder>(`v1/shops/orders/in-out/${orderId}`),
  add: (shopId: string, value: Order) => instance(CONFIG.API_ENDPOINT).put<Order>(`v1/shops/${shopId}/orders`, value),
  addExternal: (value: ExternalOrder) => instance(CONFIG.API_ENDPOINT).post<ExternalOrder>(`v1/shops/orders/in-out`, value),
  /** @deprecated Endpoint deprecated; no longer used by the UI. Scheduled for removal. */
  sendEmail: (orders: Order[]) => instance(CONFIG.API_ENDPOINT).post<SendOrderEmail>(`v1/shops/orders/notify`, { orders }),
  /** @deprecated Endpoint deprecated; no longer used by the UI. Scheduled for removal. */
  sendToPartners: (orders: Order[]) => instance(CONFIG.API_ENDPOINT).post<BatchOrderResponse>(`v1/shops/orders/send`, { orders }),
  generateOrder: (content: string) => instance(CONFIG.API_ENDPOINT).post<any>(`v1/shops/transformOrder`, { description: content }),
  update: (shopId: string, orderId: number, value: Order) => instance(CONFIG.API_ENDPOINT).put<Order>(`v1/shops/${shopId}/orders/${orderId}`, value),
  updateExternal: (orderId: number, value: ExternalOrder) => instance(CONFIG.API_ENDPOINT).put<ExternalOrder>(`v1/shops/orders/in-out/${orderId}`, value),
  delete: (shopId: string, orderId: number) => instance(CONFIG.API_ENDPOINT).delete<BodyApiResponse>(`v1/shops/${shopId}/orders/${orderId}`),
  deleteExternal: (orderId: number) => instance(CONFIG.API_ENDPOINT).delete<BodyApiResponse>(`v1/shops/orders/in-out/${orderId}`),
  uploadFile: (file: FileUploadRequest) => {
    const fName = file.filename.replace(/\s+/g, '_');
    return instance(CONFIG.API_ENDPOINT).post<FileUploadResponse>(`v1/files/orders/uploadV2`, file.file, {
      headers: {
        'Filename': fName,
      },
    })
  },
  addBatch: (orders: Order[]) => 
    instance(CONFIG.API_ENDPOINT).post<BatchOrderResponse>(`v1/shops/orders`, { orders }),
};

export default OrdersAPI;
